import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, CalendarHeart, Check, Loader2 } from "lucide-react";
import { API } from "../../App";

const todayIso = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Copenhagen" });
const EMPTY = { name: "", email: "", phone: "", desired_date: "", comment: "" };
const input = "w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,100%,45%)]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
    {children}
  </label>
);

// Venteliste når kurset ikke har planlagte hold endnu
const WaitlistForm = ({ slug, title, selfStudy }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/course-groups/${slug}/waitlist`, f);
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Tjek at alle felter er udfyldt korrekt.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-6 text-center" data-testid="waitlist-success">
        <Check className="h-10 w-10 mx-auto text-green-600" />
        <p className="mt-2 font-semibold text-slate-800">Du er skrevet på ventelisten</p>
        <p className="text-sm text-slate-600">Vi har sendt en kvittering til {f.email} og skriver til dig, så snart der er planlagt et hold.</p>
        <button onClick={() => { setF(EMPTY); setDone(false); }} data-testid="waitlist-again"
          className="mt-3 text-sm text-[hsl(210,100%,45%)] hover:underline">Skriv en mere på listen</button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[hsl(210,100%,45%)]/20 bg-[hsl(210,100%,45%)]/5 p-5" data-testid="waitlist-card">
      <div className="flex items-start gap-3 mb-4">
        <BellRing className="h-6 w-6 text-[hsl(210,100%,45%)] shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-slate-800">
            Der er ikke planlagt {selfStudy ? "prøvedatoer" : "hold"} endnu. Skriv dig på ventelisten
          </p>
          <p className="text-sm text-slate-600">Fortæl os hvilken dato der passer dig, så får du besked på mail, når holdet er klar.</p>
        </div>
      </div>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid="waitlist-form">
        <Field label="Fulde navn *">
          <input required minLength={2} value={f.name} onChange={set("name")} className={input} data-testid="waitlist-name" />
        </Field>
        <Field label="E-mail *">
          <input required type="email" value={f.email} onChange={set("email")} className={input} data-testid="waitlist-email" />
        </Field>
        <Field label="Telefon">
          <input type="tel" value={f.phone} onChange={set("phone")} className={input} data-testid="waitlist-phone" />
        </Field>
        <Field label="Ønsket startdato *">
          <input required type="date" min={todayIso()} value={f.desired_date} onChange={set("desired_date")}
            className={input} data-testid="waitlist-date" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Bemærkning">
            <textarea rows={2} value={f.comment} onChange={set("comment")} className={input} data-testid="waitlist-comment"
              placeholder="fx sprog, dag- eller aftenhold" />
          </Field>
        </div>
        <button type="submit" disabled={busy} data-testid="waitlist-submit"
          className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-white bg-[hsl(210,100%,45%)] hover:bg-[hsl(210,100%,40%)] disabled:opacity-60 transition-colors">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarHeart className="h-4 w-4" />}
          Skriv mig på ventelisten
        </button>
      </form>
    </div>
  );
};

export default WaitlistForm;
